export interface ElectricityBalance {
  chargePointTotals: number[];
  electricityTotals: number[];
  allocatedChargePointTotals: number[];
}

export interface ChargePointViewSelection {
  showConsumption: boolean;
  showChargePoints: boolean;
  splitOffPeak: boolean;
}

export function mainMeterSeries(displayedSeries: number[], originalMainMeterSeries?: number[]): number[] {
  return originalMainMeterSeries?.length ? originalMainMeterSeries : displayedSeries;
}

export function selectChargePointView(showConsumption: boolean, splitOffPeak: boolean,
                                      showChargePoints: boolean): ChargePointViewSelection {
  return {
    showConsumption,
    showChargePoints,
    splitOffPeak: showChargePoints ? false : splitOffPeak
  };
}

export const showMainMeterSummary = (_splitBySubMeters: boolean): boolean => true;

export const showChargePointOverlay = (showConsumption: boolean, showChargePoints: boolean,
                                       splitBySubMeters: boolean): boolean =>
  showConsumption && showChargePoints && !splitBySubMeters;

export const showChargePointShareGroup = (showConsumption: boolean, showChargePoints: boolean,
                                          splitBySubMeters: boolean, compareEnabled: boolean = false): boolean =>
  showConsumption && showChargePoints && !splitBySubMeters && !compareEnabled;

export const showChargePointsFromBaseline = (showChargePoints: boolean,
                                             splitBySubMeters: boolean): boolean =>
  showChargePoints && splitBySubMeters;

export const showChargePointsWithSubMeters = (showChargePoints: boolean,
                                              splitBySubMeters: boolean): boolean =>
  showChargePoints || splitBySubMeters;

export const allowOffPeakSplit = (showChargePoints: boolean, splitBySubMeters: boolean): boolean =>
  !showChargePoints && !splitBySubMeters;

export function calculateElectricityBalance(electricitySeries: number[][],
                                            chargePointSeries: number[][]): ElectricityBalance {
  const slotCount = Math.max(0, ...electricitySeries.map(series => series.length),
    ...chargePointSeries.map(series => series.length));
  const sumSlot = (series: number[][], index: number) =>
    series.reduce((sum, values) => sum + Number(values[index] || 0), 0);
  const electricityTotals = Array.from({length: slotCount}, (_, index) =>
    sumSlot(electricitySeries, index));
  const chargePointTotals = Array.from({length: slotCount}, (_, index) =>
    sumSlot(chargePointSeries, index));
  const allocatedChargePointTotals = chargePointTotals.map((value, index) =>
    Math.min(Math.max(value, 0), Math.max(electricityTotals[index], 0)));

  return {electricityTotals, chargePointTotals, allocatedChargePointTotals};
}

export function subtractChargePointShare(electricitySeries: number[][],
                                         allocatedChargePointTotals: number[]): number[][] {
  const slotCount = Math.max(0, ...electricitySeries.map(series => series.length));
  return electricitySeries.map(series => series.map((value, index) => {
    const positiveTotal = electricitySeries.reduce((sum, values) =>
      sum + Math.max(Number(values[index] || 0), 0), 0);
    if (positiveTotal <= 0) return Number(value || 0);
    const allocatedShare = Number(allocatedChargePointTotals[index] || 0)
      * Math.max(Number(value || 0), 0) / positiveTotal;
    return Math.max(Number(value || 0) - allocatedShare, 0);
  })).map(series => series.slice(0, slotCount));
}

export function electricityOutsideSelectionSeries(allocatedSeries: number[][],
                                                  selectedSeries: number[][]): number[] {
  const slotCount = Math.max(0, ...allocatedSeries.map(series => series.length),
    ...selectedSeries.map(series => series.length));
  const sumSlot = (series: number[][], index: number) =>
    series.reduce((sum, values) => sum + Number(values[index] || 0), 0);
  return Array.from({length: slotCount}, (_, index) =>
    Math.max(sumSlot(allocatedSeries, index) - sumSlot(selectedSeries, index), 0));
}
